/*
  wvr-offers-vr-availability.js — R9.0 V3 W5-R3 Slot O.

  Offers VR decision consumer. This file CONSUMES the single V3-30-owned schema emitter
  (snippets/wvr-vr-system-contract.liquid, rendered in `data` mode by
  sections/product-comparison-grid.liquid). It deliberately contains:

    - zero canonical VR system keys,
    - zero product-handle presentation exceptions,
    - zero variant IDs,
    - zero source-label aliases and zero compatibility mapping logic,
    - zero state classification: the emitter classifies, this file only displays,
    - zero network calls.

  The compatibility state and its presentation remain emitter-owned.

  CONSUMED PAYLOAD. Derived by reading the emitter source, not invented:
  `snippets/wvr-vr-system-contract.liquid` as committed at 6a4322bf65ab7144ccd0a4f347f2b2c4781165f0,
  which carries eleven canonical keys. The field names, nesting and version token are
  byte-unchanged from the ten-key 198899e4 shape this file was first written against; only the
  vocabulary grew, which is why no consumer edit was required for the eleventh key. No key is
  named anywhere in this file — naming one would be a second vocabulary.
  Every field below is validated and any mismatch fails closed: no enhancement, no hidden
  fallback, source DOM unchanged.

    <script type="application/json" id="wvr-vr-system-contract-v1" data-wvr-vr-system-contract>
    {
      "version": "wvr-vr-system/v1",
      "query":   "<url query parameter name>",
      "route":   "<offers route>",
      "anchor":  "<decision anchor id>",
      "keys":    [ { "key": "…", "label": "…", "family": "…", "magnetic_eligible": true, "url": "…" } ],
      "variant_owner": { "<variant id>": "<owning product handle>" },
      "products": {
        "<handle>": {
          "handle": "…", "resolved": true, "url": "…",
          "keys": { "<key>": { "state": "…", "variant_id": 0, "price": 0, "url": "…" } }
        }
      }
    }
    </script>

  A missing or renamed required field is a controller escalation, not a local remap here.

  TWO INVARIANTS THIS FILE ENFORCES, both anchored on the DESTINATION rather than the card:

  1. OWNERSHIP IS PROVEN AGAINST THE CTA DESTINATION PRODUCT. A `variant` may only ever be
     written onto a URL whose pathname the emitter itself attributes to the product that owns
     that variant. The card's own source handle is never sufficient: a section that routes a
     card somewhere else (market restriction) would otherwise attach product A's variant to
     product B's URL. The emitter's per-product `url` is the only source of that mapping.
  2. A MARKET-RESTRICTED CARD FAILS CLOSED, exactly as the no-JS path does. The section already
     suppresses the source-authored no-JS nav for such a card; this file suppresses the chip,
     the parameters and the CTA rewrite for the same card, so both paths make the same decision.

  URL OWNERSHIP. This slot owns exactly one query parameter (`payload.query`). A selector change
  preserves and merges every other parameter on the current URL — Shopify's own collection state
  (`sort_by`, `filter.*`, `page`) and any attribution value — and replaces only the owned one.
*/

(function () {
  'use strict';

  var NAMESPACE = 'WVROffersVrAvailability';
  var CONTRACT_VERSION = 'wvr-vr-system/v1';

  var PAYLOAD_SELECTOR = 'script[type="application/json"][data-wvr-vr-system-contract]';
  var ROOT_SELECTOR = "[data-wvr-offers-decision-scope='true'][data-wvr-offers-section]";
  var STATE_SELECTOR = '[data-wvr-offers-vr-state]';
  var STATUS_SELECTOR = '[data-wvr-offers-vr-status]';
  var CONTROL_SELECTOR = '[data-wvr-offers-vr-control]';
  var CARD_SELECTOR = '.cpc-wrapper .cpc-card';
  var DECISION_SELECTOR = '.cpc-decision';
  var ACTIONS_SELECTOR = '.cpc-actions';
  var PRIMARY_ANCHOR_SELECTOR = '.cpc-actions a[href]';
  var CONFIGURATION_CHOICE_SELECTOR = '[data-wvr-offer-configuration-choice]';
  var READY_ATTRIBUTE = 'data-wvr-offers-vr-ready';
  var VARIANT_PARAM = 'variant';

  // The emitter's four state tokens, paired with the Controller Contract 001 section 4.2
  // display copy. This is presentation only — no state is derived, inferred or reclassified
  // here, and a token outside this table produces no claim at all.
  var STATE_COPY = {
    'available': 'Available',
    'pre-order': 'Pre-order',
    'out-of-stock': 'Out of stock',
    'not-compatible': 'Not compatible'
  };
  var STATE_NOT_COMPATIBLE = 'not-compatible';
  var STATE_AVAILABLE = 'available';
  var STATE_PREORDER = 'pre-order';

  var CONTROL_LABEL = 'Filter by your VR system';
  var CONTROL_PROMPT = 'Select your VR system';

  var LOWERCASE_MAP = {
    A: 'a', B: 'b', C: 'c', D: 'd', E: 'e', F: 'f', G: 'g', H: 'h', I: 'i',
    J: 'j', K: 'k', L: 'l', M: 'm', N: 'n', O: 'o', P: 'p', Q: 'q', R: 'r',
    S: 's', T: 't', U: 'u', V: 'v', W: 'w', X: 'x', Y: 'y', Z: 'z'
  };

  /* ---------------------------------------------------------------- helpers */

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isFilledString(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  // Explicit ASCII fold. toLocaleLowerCase is locale-sensitive and would fold I to a dotless
  // form under a Turkish locale, which is exactly the class of silent mismatch this rejects.
  function asciiLower(value) {
    var out = '';
    for (var index = 0; index < value.length; index += 1) {
      var character = value.charAt(index);
      out += Object.prototype.hasOwnProperty.call(LOWERCASE_MAP, character) ? LOWERCASE_MAP[character] : character;
    }
    return out;
  }

  // Variant ids arrive as either a JSON number or a JSON string; both are compared as strings.
  function variantIdOf(value) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
    if (typeof value === 'number' && isFinite(value) && value > 0) return String(value);
    return null;
  }

  function pathnameOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return null;
    }
  }

  function stateCopyFor(product, key, state) {
    return STATE_COPY[state];
  }

  // The emitter-declared product handle owning a pathname, or null when the emitter never
  // declared it or two products claim it. Own-property lookup only: a pathname such as
  // /constructor must never resolve through the prototype chain.
  function pathHandleOf(payload, path) {
    if (!Object.prototype.hasOwnProperty.call(payload.pathHandles, path)) return null;
    return payload.pathHandles[path];
  }

  /* -------------------------------------------------------------- the payload */

  // Uniqueness guard plus full structural validation. Returns null on any failure; a null
  // payload means fail closed and the caller must not touch the DOM.
  function readPayload() {
    var nodes = document.querySelectorAll(PAYLOAD_SELECTOR);
    if (nodes.length !== 1) return null;

    var parsed;
    try {
      parsed = JSON.parse(nodes[0].textContent);
    } catch (error) {
      return null;
    }

    if (!isPlainObject(parsed)) return null;
    if (parsed.version !== CONTRACT_VERSION) return null;
    if (!isFilledString(parsed.query)) return null;
    if (!isFilledString(parsed.route) || parsed.route.charAt(0) !== '/') return null;
    if (!isFilledString(parsed.anchor)) return null;
    if (!isPlainObject(parsed.variant_owner)) return null;
    if (!isPlainObject(parsed.products)) return null;

    if (!Array.isArray(parsed.keys) || parsed.keys.length === 0) return null;
    var keys = [];
    var keySet = Object.create(null);
    var magneticEligibility = Object.create(null);
    for (var k = 0; k < parsed.keys.length; k += 1) {
      var entry = parsed.keys[k];
      if (!isPlainObject(entry)) return null;
      if (!isFilledString(entry.key) || !isFilledString(entry.label)) return null;
      if (typeof entry.magnetic_eligible !== 'boolean') return null;
      // The emitter owns the vocabulary, so its keys must already be in canonical form.
      if (entry.key !== asciiLower(entry.key.trim())) return null;
      if (keySet[entry.key]) return null;
      keySet[entry.key] = entry.label;
      magneticEligibility[entry.key] = entry.magnetic_eligible;
      keys.push({ key: entry.key, label: entry.label });
    }

    var owner = Object.create(null);
    var ownerIds = Object.keys(parsed.variant_owner);
    for (var o = 0; o < ownerIds.length; o += 1) {
      if (!isFilledString(parsed.variant_owner[ownerIds[o]])) return null;
      owner[ownerIds[o]] = parsed.variant_owner[ownerIds[o]];
    }

    var products = Object.create(null);
    // Pathname → owning product handle, built from the emitter's own per-product `url`. This is
    // what makes an ownership proof possible against the destination a CTA actually points at.
    // A pathname claimed by more than one product is ambiguous and is struck out entirely, so
    // ambiguity can never resolve to a variant.
    var pathHandles = Object.create(null);
    var handles = Object.keys(parsed.products);
    if (handles.length === 0) return null;
    for (var p = 0; p < handles.length; p += 1) {
      var record = parsed.products[handles[p]];
      if (!isPlainObject(record)) return null;
      if (typeof record.resolved !== 'boolean') return null;
      if (!isPlainObject(record.keys)) return null;

      var states = Object.create(null);
      var mapped = Object.keys(record.keys);
      for (var m = 0; m < mapped.length; m += 1) {
        // A state for a key the emitter did not declare is a broken payload, not a state.
        if (!keySet[mapped[m]]) return null;
        var cell = record.keys[mapped[m]];
        if (!isPlainObject(cell)) return null;
        if (!isFilledString(cell.state)) return null;
        var variantId = variantIdOf(cell.variant_id);
        var priceFormatted = null;
        if (variantId) {
          if (!isFilledString(cell.price_formatted)) return null;
          priceFormatted = cell.price_formatted;
        }
        states[mapped[m]] = {
          state: cell.state,
          variantId: variantId,
          priceFormatted: priceFormatted
        };
      }

      var handle = isFilledString(record.handle) ? record.handle : handles[p];
      products[handles[p]] = {
        handle: handle,
        resolved: record.resolved,
        keys: states
      };

      if (isFilledString(record.url)) {
        var recordPath = pathnameOf(record.url);
        if (recordPath) {
          pathHandles[recordPath] = Object.prototype.hasOwnProperty.call(pathHandles, recordPath)
            && pathHandles[recordPath] !== handle
            ? null
            : handle;
        }
      }
    }

    return {
      query: parsed.query,
      route: parsed.route,
      anchor: parsed.anchor,
      keys: keys,
      keySet: keySet,
      magneticEligibility: magneticEligibility,
      owner: owner,
      products: products,
      pathHandles: pathHandles
    };
  }

  /*
    For a resolved key and a product, the emitter's own cell. Nothing is classified here.
    Returns { state, variantId } where state is null when no claim may be made:
    an unresolved product, a key the emitter emitted no cell for, or a state token outside
    the contracted four.

    `destinationHandle` is the product the CTA's own pathname belongs to, resolved from the
    emitter's per-product URLs — NOT the card's source handle. The variant is proven against
    that, so a variant can never be written onto a URL the emitter attributes to another
    product. When the destination is unknown or ambiguous, no variant is ever proven.
  */
  function resolveCell(payload, product, key, destinationHandle) {
    if (!product.resolved) return { state: null, variantId: null, priceFormatted: null };
    var cell = product.keys[key];
    if (!cell) return { state: null, variantId: null, priceFormatted: null };
    if (!Object.prototype.hasOwnProperty.call(STATE_COPY, cell.state)) return { state: null, variantId: null, priceFormatted: null };
    // Ownership proof: the flat index must name the CTA's DESTINATION product as the owner.
    var proven = cell.variantId
      && isFilledString(destinationHandle)
      && payload.owner[cell.variantId] === destinationHandle
      ? cell.variantId
      : null;
    return {
      state: cell.state,
      variantId: proven,
      priceFormatted: proven ? cell.priceFormatted : null
    };
  }

  /* ------------------------------------------------------------------- init */

  function init(root) {
    var sectionId = root.getAttribute('data-wvr-offers-section');
    if (!sectionId) return;

    var registry = window[NAMESPACE];
    if (registry[sectionId]) registry[sectionId].abort();
    var controller = new AbortController();
    var signal = controller.signal;
    registry[sectionId] = controller;

    document.addEventListener('shopify:section:unload', function (event) {
      if (!event.detail || event.detail.sectionId !== sectionId) return;
      controller.abort();
      delete registry[sectionId];
    }, { signal: signal });

    var payload = readPayload();
    if (!payload) return;

    var containers = [].slice.call(root.querySelectorAll(STATE_SELECTOR));
    var cards = [].slice.call(root.querySelectorAll(CARD_SELECTOR));
    if (containers.length === 0 || cards.length === 0) return;

    var control = root.querySelector(CONTROL_SELECTOR);
    if (!control) return;
    control.replaceChildren();

    // Validate every pairing before mutating anything, so a rejected card cannot leave the
    // surface half-upgraded.
    var pairs = [];
    for (var c = 0; c < containers.length; c += 1) {
      var container = containers[c];
      var cardId = container.getAttribute('data-wvr-offers-card-id');
      var card = null;
      if (!cardId) continue;
      for (var cardIndex = 0; cardIndex < cards.length; cardIndex += 1) {
        if (cards[cardIndex].getAttribute('data-wvr-offers-card-id') === cardId) {
          card = cards[cardIndex];
          break;
        }
      }
      if (!card) continue;

      var decision = card.querySelector(DECISION_SELECTOR);
      var actions = decision ? decision.querySelector(ACTIONS_SELECTOR) : null;
      var anchor = card.querySelector(PRIMARY_ANCHOR_SELECTOR);
      var status = container.querySelector(STATUS_SELECTOR);
      if (!decision || !actions || !anchor || !status) continue;

      var sourceUrl = container.getAttribute('data-wvr-offers-vr-url');
      var sourcePath = pathnameOf(sourceUrl);
      var anchorPath = pathnameOf(anchor.getAttribute('href'));
      if (!sourcePath || !anchorPath) continue;
      if (sourcePath !== anchorPath) continue;

      var product = payload.products[container.getAttribute('data-wvr-offers-vr-handle')];
      if (!product) continue;

      // The product the CTA destination actually belongs to, per the emitter's own URLs. It must
      // exist, be unambiguous, and be this card's product; anything else means the section and
      // the emitter disagree about what this card links to, and no claim may be made.
      var destinationHandle = pathHandleOf(payload, anchorPath);
      if (product.resolved && (!destinationHandle || destinationHandle !== product.handle)) continue;

      var priceOptions = [].slice.call(card.querySelectorAll('[data-wvr-offer-price-configuration]')).map(function (option) {
        var variants = [];
        try { variants = JSON.parse(option.querySelector('[data-wvr-option-variants]').textContent); } catch (error) {}
        return {
          option: option,
          choice: option.querySelector(CONFIGURATION_CHOICE_SELECTOR),
          handle: option.getAttribute('data-wvr-offer-price-product'),
          variants: variants,
          campaign: option.querySelector('.cpc-offer-price-option__campaign'),
          compare: option.querySelector('s'),
          saving: option.querySelector('.cpc-offer-price-option__saving'),
          availability: option.querySelector('[data-wvr-option-availability]')
        };
      });

      pairs.push({
        vrSelect: card.querySelector('[data-wvr-card-vr-select]'),
        key: null,
        card: card,
        container: container,
        status: status,
        decision: decision,
        actions: actions,
        anchor: anchor,
        product: product,
        destinationHandle: destinationHandle,
        magneticOptions: [].slice.call(card.querySelectorAll('[data-wvr-offer-price-configuration="magnetic"]')),
        configurationChoices: [].slice.call(card.querySelectorAll(CONFIGURATION_CHOICE_SELECTOR)),
        configurationGroup: card.querySelector('.cpc-offer-price-options'),
        choicePrompt: card.querySelector('[data-wvr-offer-choice-prompt]'),
        priceOptions: priceOptions,
        fromPrice: card.querySelector('[data-wvr-offer-from-price]'),
        mountHeading: card.querySelector('[data-wvr-offer-mount-heading]'),
        systemPrompt: card.querySelector('[data-wvr-offer-system-prompt]'),
        // Every CTA is rebuilt from the section-authored canonical product URL, so no handle
        // URL is synthesized in JavaScript.
        baseHref: anchor.getAttribute('href'),
        basePath: anchorPath
      });
    }

    if (pairs.length === 0) return;

    /* ----------------------------------------------------------- the control */

    var inner = document.createElement('div');
    inner.className = 'cpc-vr-control-inner';

    var selectId = 'wvr-offers-vr-system-' + sectionId;
    var label = document.createElement('label');
    label.setAttribute('for', selectId);
    label.textContent = CONTROL_LABEL;

    var select = document.createElement('select');
    select.id = selectId;
    select.name = payload.query;
    select.setAttribute('data-wvr-offers-vr-select', '');

    var prompt = document.createElement('option');
    prompt.value = '';
    prompt.textContent = CONTROL_PROMPT;
    select.appendChild(prompt);

    for (var i = 0; i < payload.keys.length; i += 1) {
      var option = document.createElement('option');
      option.value = payload.keys[i].key;
      option.textContent = payload.keys[i].label;
      select.appendChild(option);
    }

    inner.appendChild(label);
    inner.appendChild(select);
    control.appendChild(inner);
    control.hidden = false;

    pairs.forEach(function (pair) {
      if (!pair.vrSelect) return;
      while (pair.vrSelect.options.length > 1) pair.vrSelect.remove(1);
      payload.keys.forEach(function (entry) {
        var option = document.createElement('option');
        option.value = entry.key;
        option.textContent = entry.label;
        pair.vrSelect.appendChild(option);
      });
      pair.key = currentKey();
      pair.vrSelect.value = pair.key || '';
      pair.vrSelect.addEventListener('change', function () {
        pair.key = pair.vrSelect.value || null;
        if (pair.systemPrompt) pair.systemPrompt.hidden = true;
        pair.vrSelect.removeAttribute('aria-invalid');
        if (pair.choicePrompt) pair.choicePrompt.hidden = true;
        pair.configurationChoices.forEach(function (choice) { choice.checked = false; });
        render();
      }, { signal: signal });
    });

    // Relocate each state container into its card's existing decision area, above the actions.
    for (var r = 0; r < pairs.length; r += 1) {
      pairs[r].decision.insertBefore(pairs[r].container, pairs[r].actions);
      pairs[r].container.hidden = false;
    }

    /* ------------------------------------------------------------ rendering */

    function currentKey() {
      var params;
      var values;
      try {
        params = new URLSearchParams(window.location.search);
        values = params.getAll(payload.query);
      } catch (error) {
        return null;
      }
      if (values.length === 0) return null;

      // A repeated owned parameter is ambiguous, and a single value outside the emitted
      // vocabulary is unsupported. Both fail closed and are removed immediately so a retired
      // or malformed system token cannot remain shareable in the Offers URL. URLSearchParams
      // deletes every repetition while preserving every unrelated parameter; pathname and hash
      // are copied verbatim.
      if (values.length === 1) {
        var candidate = asciiLower(values[0].trim());
        if (payload.keySet[candidate]) return candidate;
      }

      params.delete(payload.query);
      var query = params.toString();
      var next = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      var current = window.location.pathname + window.location.search + window.location.hash;
      if (next !== current) {
        try {
          window.history.replaceState(window.history.state, '', next);
        } catch (error) {
          // The UI still fails closed even if history is unavailable.
        }
      }
      return null;
    }

    function selectedConfiguration(pair) {
      for (var choiceIndex = 0; choiceIndex < pair.configurationChoices.length; choiceIndex += 1) {
        var choice = pair.configurationChoices[choiceIndex];
        var option = choice.closest('[data-wvr-offer-price-configuration]');
        if (choice.checked && !choice.disabled && option && !option.hidden) return choice;
      }
      return null;
    }

    function applyCta(pair, key, cell) {
      var choice = selectedConfiguration(pair);
      var choiceHref = choice ? choice.getAttribute('data-wvr-offer-product-url') : pair.baseHref;
      var url;
      try {
        url = new URL(choiceHref, window.location.origin);
      } catch (error) {
        return;
      }

      var allowedPath = false;
      for (var choiceIndex = 0; choiceIndex < pair.configurationChoices.length; choiceIndex += 1) {
        if (pathnameOf(pair.configurationChoices[choiceIndex].getAttribute('data-wvr-offer-product-url')) === url.pathname) {
          allowedPath = true;
          break;
        }
      }
      if (!allowedPath && url.pathname !== pair.basePath) return;

      if (url.origin === window.location.origin && /(?:^|\/)products\/[^/]+\/?$/.test(url.pathname)) {
        var previewIds = new URLSearchParams(window.location.search).getAll('preview_theme_id');
        var previewId = previewIds.length === 1 && /^[1-9]\d*$/.test(previewIds[0]) ? previewIds[0] : '';
        if (previewIds.length === 0 && window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'unpublished') {
          var currentThemeId = window.Shopify.theme.id;
          if ((typeof currentThemeId === 'string' || typeof currentThemeId === 'number') && /^[1-9]\d*$/.test(String(currentThemeId))) {
            previewId = String(currentThemeId);
          }
        }
        if (previewId) url.searchParams.set('preview_theme_id', previewId);
      }

      if (!key || (cell && cell.state === STATE_NOT_COMPATIBLE)) {
        // No key, or the key does not map to this product: carry neither parameter.
        url.searchParams.delete(payload.query);
        url.searchParams.delete(VARIANT_PARAM);
      } else {
        url.searchParams.set(payload.query, key);
        var variantId = choice && choice.getAttribute('data-wvr-selected-variant');
        if (variantId) url.searchParams.set(VARIANT_PARAM, variantId);
        else url.searchParams.delete(VARIANT_PARAM);
      }

      pair.anchor.setAttribute('href', url.pathname + url.search + url.hash);
    }

    function render() {
      select.value = currentKey() || '';

      for (var index = 0; index < pairs.length; index += 1) {
        var pair = pairs[index];
        var key = pair.vrSelect ? pair.key : currentKey();
        if (pair.vrSelect) pair.vrSelect.value = key || '';
        var cell = key ? resolveCell(payload, pair.product, key, pair.destinationHandle) : null;
        var visibleOptions = [];
        pair.priceOptions.forEach(function (price) {
          var magnetic = price.choice.value === 'magnetic';
          var visible = !!key && (!magnetic || payload.magneticEligibility[key] === true);
          price.option.hidden = !visible;
          if (!visible) {
            price.choice.checked = false;
            price.choice.removeAttribute('data-wvr-selected-variant');
            return;
          }
          visibleOptions.push(price);
          var targetProduct = payload.products[price.handle];
          var targetCell = targetProduct ? resolveCell(payload, targetProduct, key, price.handle) : null;
          var variant = targetCell && targetCell.variantId
            ? price.variants.find(function (entry) { return entry.id === targetCell.variantId; })
            : null;
          if (!targetProduct && magnetic && price.variants.length === 1) variant = price.variants[0];
          var sellable = !!variant && variant.available && variant.price > 0;
          if (targetProduct && (!targetCell || (targetCell.state !== STATE_AVAILABLE && targetCell.state !== STATE_PREORDER))) sellable = false;
          price.choice.disabled = !sellable;
          price.choice.setAttribute('data-wvr-selected-variant', variant ? variant.id : '');
          if (!sellable) price.choice.checked = false;
          price.option.setAttribute('data-wvr-offer-price-state', sellable ? 'ready' : 'unavailable');
          price.option.setAttribute('data-wvr-offer-price-source', 'selected-variant');
          var detail = price.option.querySelector('.cpc-offer-price-option__label > span');
          if (detail) detail.textContent = payload.keySet[key];
          if (price.campaign) {
            price.campaign.hidden = !variant;
            price.campaign.textContent = variant ? variant.priceFormatted : '';
          }
          var hideCampaignDiscount = document.documentElement.dataset.wvrEvergreen === 'true' && 'onestock-s,wield-vr-onestock,onestock-magnetic,wield-vr-onestock-pro,onestock-pro-magnetic,wield-vr-onestock-ultimate-edition,onestock-ultimate-magnetic'.split(',').indexOf(price.option.dataset.wvrOfferPriceProduct) !== -1;
          if (price.compare) {
            var showCompare = !hideCampaignDiscount && variant && variant.compareFormatted;
            price.compare.hidden = !showCompare;
            price.compare.textContent = showCompare || '';
          }
          if (price.saving) {
            var showSaving = !hideCampaignDiscount && variant && variant.savingFormatted;
            price.saving.hidden = !showSaving;
            price.saving.textContent = showSaving || '';
          }
          if (price.availability) {
            var state = sellable ? (targetCell && targetCell.state === STATE_PREORDER ? 'preorder' : 'available') : 'unavailable';
            price.availability.dataset.state = state;
            price.availability.textContent = price.availability.dataset[state];
          }
        });
        var single = visibleOptions.length === 1;
        if (single && !visibleOptions[0].choice.disabled) visibleOptions[0].choice.checked = true;
        if (pair.configurationGroup) {
          pair.configurationGroup.hidden = !key;
          pair.configurationGroup.dataset.single = single ? 'true' : 'false';
          pair.configurationGroup.setAttribute('role', single ? 'group' : 'radiogroup');
          if (single) {
            pair.configurationGroup.removeAttribute('aria-labelledby');
            pair.configurationGroup.setAttribute('aria-label', visibleOptions[0].option.querySelector('strong').textContent);
          } else {
            pair.configurationGroup.removeAttribute('aria-label');
            if (pair.mountHeading) pair.configurationGroup.setAttribute('aria-labelledby', pair.mountHeading.id);
          }
        }
        if (pair.fromPrice) pair.fromPrice.hidden = !!key;
        if (pair.mountHeading) pair.mountHeading.hidden = !key || visibleOptions.length < 2;
        if (single || !key) {
          if (pair.choicePrompt) pair.choicePrompt.hidden = true;
          if (pair.configurationGroup) pair.configurationGroup.removeAttribute('aria-invalid');
        }
        pair.container.hidden = true;
        var unavailable = !!key && !visibleOptions.some(function (price) { return !price.choice.disabled; });
        if (unavailable) pair.anchor.setAttribute('aria-disabled', 'true');
        else pair.anchor.removeAttribute('aria-disabled');
        pair.anchor.classList.toggle('cpc-button--sold-out', unavailable);

        applyCta(pair, key, cell);
        pair.anchor.closest('[data-wvr-offers-card-id]')?.dispatchEvent(new CustomEvent('wvr:offer-configuration-change'));
      }
    }

    select.addEventListener('change', function () {
      var chosen = select.value;
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return;
      }
      // Preserve and merge. This slot owns exactly one parameter, so the current query is carried
      // through in source order and only the owned key is rewritten: Shopify's own collection
      // state on this template (`sort_by`, `filter.*`, `page`) and every attribution parameter
      // survive a selector change. `delete` drops every repetition of the owned key, so a URL
      // carrying it more than once normalizes to exactly one owned value and nothing else moves.
      params.delete(payload.query);
      if (chosen && payload.keySet[chosen]) params.set(payload.query, chosen);
      var query = params.toString();
      var next = payload.route + (query ? '?' + query : '') + '#' + payload.anchor;
      // pushState never scrolls and never moves focus; the select keeps it.
      window.history.pushState({}, '', next);
      pairs.forEach(function (pair) {
        pair.key = chosen || null;
        pair.configurationChoices.forEach(function (choice) { choice.checked = false; });
      });
      render();
    }, { signal: signal });

    root.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;
      var choiceOption = event.target.closest('[data-wvr-offer-price-configuration]');
      if (!choiceOption || !root.contains(choiceOption) || choiceOption.hidden) return;
      var choice = choiceOption.querySelector(CONFIGURATION_CHOICE_SELECTOR);
      if (!choice || choice.disabled || choice.checked) return;
      choice.checked = true;
      choice.dispatchEvent(new Event('change', { bubbles: true }));
    }, { capture: true, signal: signal });

    for (var pairIndex = 0; pairIndex < pairs.length; pairIndex += 1) {
      (function (pair) {
        pair.anchor.addEventListener('click', function (event) {
          var key = pair.vrSelect ? pair.key : currentKey();
          if (!key) {
            event.preventDefault();
            if (pair.systemPrompt) pair.systemPrompt.hidden = false;
            if (pair.vrSelect) {
              pair.vrSelect.setAttribute('aria-invalid', 'true');
              pair.vrSelect.setAttribute('aria-describedby', pair.systemPrompt.id);
              pair.vrSelect.focus();
              pair.vrSelect.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
            return;
          }
          if (selectedConfiguration(pair)) return;
          event.preventDefault();
          if (pair.anchor.getAttribute('aria-disabled') === 'true') return;
          if (pair.configurationGroup && pair.configurationGroup.dataset.single === 'true') return;
          if (pair.choicePrompt) pair.choicePrompt.hidden = false;
          if (pair.configurationGroup) {
            pair.configurationGroup.setAttribute('aria-invalid', 'true');
            pair.configurationGroup.classList.remove('cpc-offer-price-options--attention');
            void pair.configurationGroup.offsetWidth;
            pair.configurationGroup.classList.add('cpc-offer-price-options--attention');
          }
          for (var focusIndex = 0; focusIndex < pair.configurationChoices.length; focusIndex += 1) {
            var focusChoice = pair.configurationChoices[focusIndex];
            var focusOption = focusChoice.closest('[data-wvr-offer-price-configuration]');
            if (!focusChoice.disabled && focusOption && !focusOption.hidden) {
              focusChoice.focus();
              break;
            }
          }
        }, { signal: signal });

        for (var choiceIndex = 0; choiceIndex < pair.configurationChoices.length; choiceIndex += 1) {
          (function (choice) {
            choice.addEventListener('change', function () {
            if (pair.choicePrompt) pair.choicePrompt.hidden = true;
            if (pair.configurationGroup) {
              pair.configurationGroup.removeAttribute('aria-invalid');
              pair.configurationGroup.classList.remove('cpc-offer-price-options--attention');
            }
            render();
            }, { signal: signal });
          })(pair.configurationChoices[choiceIndex]);
        }
      })(pairs[pairIndex]);
    }

    window.addEventListener('popstate', function () {
      pairs.forEach(function (pair) { pair.key = currentKey(); });
      render();
    }, { signal: signal });

    render();
    root.setAttribute(READY_ATTRIBUTE, 'true');
  }

  function boot() {
    window[NAMESPACE] = window[NAMESPACE] || {};
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    for (var index = 0; index < roots.length; index += 1) {
      init(roots[index]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
